import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { FormField, SelectionList, Spinner, Text, } from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useUnmount } from 'react-use';
import { HookFormSelectionList } from 'components/hookForm';
import { useBreakpointValue } from 'utils/mediaQuery';
import { OAuthForm } from './components/form/oAuthForm';
import { useLazyReadOAuthProvider } from './utils/useLazyReadOAuthProvider/useLazyReadOAuthProvider';
import { useLazyReadOAuthStrategySchema } from './utils/useLazyReadOAuthStrategySchema/useLazyReadOAuthStrategySchema';
import { useReadOAuthStrategies } from './utils/useReadOAuthStrategies/useReadOAuthStrategies';
export const OAuth = ({ disabled, parentOAuthId, onChangeParentOAuthId, onChangeOAuthStrategy, handleParentSubmit, meta, }) => {
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const { reset: resetOAuthForm } = useFormContext();
    const { strategies, isStrategiesError } = useReadOAuthStrategies();
    const strategiesForm = useForm({
        defaultValues: { selectedStrategy: [] },
    });
    const [selectedStrategy] = useWatch({
        control: strategiesForm.control,
        name: 'selectedStrategy',
    });
    const { readOAuthStrategySchema, oAuthSchema, setOAuthSchemaSchema, isSchemaLoading, isOAuthSchemaError, } = useLazyReadOAuthStrategySchema();
    const { readOAuthProvider, oAuthProvider, setOAuthProvider, isOAuthProviderLoading, isOAuthProviderError, } = useLazyReadOAuthProvider();
    useUnmount(() => {
        resetOAuthState();
        onChangeParentOAuthId(undefined);
    });
    const resetOAuthState = () => {
        setOAuthProvider(undefined);
        setOAuthSchemaSchema([]);
        strategiesForm.reset({ selectedStrategy: [] });
        onChangeOAuthStrategy(undefined);
        resetOAuthForm({});
    };
    const gqlErrorMessage = () => {
        if (isStrategiesError) {
            return 'Something went wrong fetching the strategies';
        }
        if (isOAuthSchemaError) {
            return 'Something went wrong fetching the schema';
        }
        if (isOAuthProviderError) {
            return 'Something went wrong fetching the provider';
        }
        return undefined;
    };
    // Getting the OAuth provider from the OAuth id
    useEffect(() => {
        if (parentOAuthId && !oAuthProvider && !isOAuthProviderLoading) {
            readOAuthProvider({
                variables: {
                    id: parentOAuthId,
                },
            });
        }
    }, [parentOAuthId, oAuthProvider, isOAuthProviderLoading, readOAuthProvider]);
    // Setting the strategiesForm state to the strategy from the oAuthProvider
    useEffect(() => {
        if (!isOAuthProviderLoading && oAuthProvider && strategies.length > 0) {
            strategiesForm.reset({
                selectedStrategy: [
                    strategies === null || strategies === void 0 ? void 0 : strategies.find((strategy) => strategy.name === oAuthProvider.strategy),
                ] || [],
            });
            onChangeOAuthStrategy(oAuthProvider.strategy);
        }
    }, [oAuthProvider, isOAuthProviderLoading, strategies]);
    // Get the schema once the user selected a strategy
    useEffect(() => {
        if (selectedStrategy === null || selectedStrategy === void 0 ? void 0 : selectedStrategy.name) {
            readOAuthStrategySchema({
                variables: {
                    id: selectedStrategy.name,
                },
            });
            onChangeOAuthStrategy(selectedStrategy.name);
        }
    }, [selectedStrategy, readOAuthStrategySchema]);
    return (_jsxs(_Fragment, { children: [_jsx(FormField, Object.assign({ span: isSmallScreen ? 6 : 3 }, { children: _jsx(HookFormSelectionList, Object.assign({ label: "OAuth strategies", name: "selectedStrategy", control: strategiesForm.control, disabled: disabled, componentProps: {
                        clearable: true,
                        placeholder: 'Select an OAuth strategy',
                        getItemTitleByValue: (strategy) => (_jsx(Text.Paragraph, { children: strategy.label })),
                        equalityFn: (item1, item2) => item1.name === item2.name,
                    }, meta: {
                        warning: (meta === null || meta === void 0 ? void 0 : meta.error) || gqlErrorMessage(),
                    } }, { children: strategies.map((strategy) => (_jsx(SelectionList.Item, Object.assign({ value: strategy }, { children: _jsx(Text.Paragraph, { children: strategy.label }) }), strategy.name))) })) })), (selectedStrategy === null || selectedStrategy === void 0 ? void 0 : selectedStrategy.name) &&
                (isSchemaLoading ? (_jsx(Spinner, {})) : (_jsx(FormField, { children: _jsx(OAuthForm, { disabled: disabled, selectedStrategy: selectedStrategy, oAuthSchema: oAuthSchema, oAuthProvider: oAuthProvider, handleParentSubmit: handleParentSubmit, onChangeParentOAuthId: onChangeParentOAuthId, setOAuthProvider: setOAuthProvider }) })))] }));
};
